import type { AnalysisResult, AnalysisStatus } from "@/types/scribe/Analysis";

export type ExcludedDocument = { fileId: string; fileName: string | null; sizeBytes: number };

// LSP-17305: a bare "Scheduled" chip reads as actively running — a customer watched its elapsed
// timer climb to 342 minutes and reported the report as hung, when it was only waiting on media.
// Shared here since both the EntityReports table and SparkyReportCard render this chip.
export const SCHEDULED_WAITING_FOR_MEDIA_LABEL = "Scheduled · waiting for media";

// LSP-16433: render an omitted document's size defensively so a malformed content-length never leaks
// "NaN MB"/"Infinity MB"/"-2 MB" into user-facing copy. Returns "" for an unusable size so callers can
// fall back to just the filename. Shared by the report chip (ExcludedDocumentsNotice) and the run toasts.
export function formatExcludedDocumentSize(sizeBytes: number): string {
	if (!Number.isFinite(sizeBytes) || sizeBytes < 0) {
		return "";
	}
	if (sizeBytes === 0) {
		return "0 MB";
	}
	const megabytes = sizeBytes / (1024 * 1024);
	return megabytes < 1 ? "<1 MB" : `${Math.round(megabytes)} MB`;
}

export type ScheduledAnalysisItem = {
	id: string;
	conversationId: string;
	conversationName?: string | null;
	packId?: string | null;
	packName: string | null;
	status: string;
	createdAt: string | Date;
	ownerLabel?: string;
	excludedDocuments?: ExcludedDocument[];
	/** True when this item came from the legacy AnalysisSchedules table and has no scribe analysisId yet. Cancel uses the legacy endpoint. */
	isLegacySchedule?: boolean;
};

export type RowItem = {
	id: string;
	name: string;
	title?: string;
	createdAt: string | Date;
	updatedAt?: string | Date;
	status: AnalysisStatus;
	owner: string | undefined;
	externalId?: string;
	externalType?: string;
	packId?: string;
	isScheduled?: boolean;
	isRealTime?: boolean;
	sessionId?: string;
	mediaCompleteAt?: Date | string | null;
	deferredMedia?: boolean;
	excludedDocuments?: ExcludedDocument[];
	_statusBecameInProgressAt?: Date;
	_analysis?: AnalysisResult;
	_scheduledAnalysis?: ScheduledAnalysisItem;
};
